import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, RouterLink } from '@angular/router';

import { Store } from '@ngrx/store';

import { QuotesActions } from '../../../@store/quotes/quotes.actions';
import {
  selectItemProcessesCategories,
  selectSavedNote,
  selectSelectedCANumber,
  selectSelectedQuote,
  selectSelectedQuoteError,
  selectSelectedQuoteLoading,
} from '../../../@store/quotes/quotes.selectors';
import { CONFIG } from '../../../config';
import { SafeAny } from '../../../types/safe-any';
import { ChangeQuoteStatusDialogComponent } from '../change-quote-status-dialog/change-quote-status-dialog.component';
import { ConstructionTextComponent } from '../construction-text/construction-text.component';
import { DeleteQuoteDialogComponent } from '../delete-quote-dialog/delete-quote-dialog.component';
import { DuplicateQuoteDialogComponent } from '../duplicate-quote-dialog/duplicate-quote-dialog.component';
import { QuoteDetailAccordionComponent } from '../quote-detail-accordion/quote-detail-accordion.component';
import { QuoteTotalTabsComponent } from '../quote-total-tabs/quote-total-tabs.component';

@Component({
  selector: 'ciwc-view-quotes',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatIconModule,
    RouterLink,
    QuoteTotalTabsComponent,
    QuoteDetailAccordionComponent,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatDialogModule,
    MatExpansionModule,
    ConstructionTextComponent,
    MatCheckboxModule,
  ],
  templateUrl: './view-quotes.component.html',
  styleUrl: './view-quotes.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ViewQuotesComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  private readonly store = inject(Store);
  private readonly fb = inject(FormBuilder);
  private readonly dialog = inject(MatDialog);
  readonly route = inject(ActivatedRoute);
  readonly selectedQuote = this.store.selectSignal(selectSelectedQuote);
  readonly selectedQuoteLoading = this.store.selectSignal(selectSelectedQuoteLoading);
  readonly selectedQuoteError = this.store.selectSignal(selectSelectedQuoteError);
  readonly itemProcessesCategories = this.store.selectSignal(selectItemProcessesCategories);
  readonly savedNote = this.store.selectSignal(selectSavedNote);
  readonly selectedCANumber = this.store.selectSignal(selectSelectedCANumber);
  readonly CONFIG = CONFIG;

  errorText = CONFIG.ERROR_MESSAGES.DEFAULT;
  displayNotes = signal<SafeAny[]>([]);
  constructionSheet = signal<SafeAny>([]);
  processedConstructionSteps = signal<SafeAny>([]);
  constructionText = signal('');
  remainingDescriptions = signal<string[]>([]);
  notesForm = this.fb.group({
    notes: [''],
  });

  // Data signals for accordion categories
  fillersAndMiscLaborData = signal<SafeAny[]>([]);
  copperData = signal<SafeAny[]>([]);
  singleEndData = signal<SafeAny[]>([]);
  compoundsData = signal<SafeAny[]>([]);
  teflonTapesData = signal<SafeAny[]>([]);
  allTapesAndFillersData = signal<SafeAny[]>([]);

  // Total prices for quote calculation
  totalPrices = signal<number[]>([0]);

  private readonly categoryColors: Record<string, string> = {
    OTHER: '#8a8a8a',
    COPPER: '#b73a3a',
    SINGLE_END: '#2e9d50',
    COMPOUNDS: '#a89334',
    TEFLON_TAPES: '#b05a33',
    ALL_TAPES_AND_FILLERS: '#9c4b9c',
  };

  // Quote detail accordions signal
  quoteDetailAccordions = signal<SafeAny[]>([]);

  constructor() {
    effect(() => {
      const selectedQuote = this.selectedQuote();

      if (selectedQuote?.itemProcesses) {
        this.setDetailsAccordionData(selectedQuote.itemProcesses);
        this.updateAccordions();
        this.calculateTotalPrices();
      }

      // Process construction data if CA number exists
      if (selectedQuote?.caNumber && !selectedQuote.caCustomDescription) {
        // Dispatch action to load CA number with relations
        this.store.dispatch(
          QuotesActions.loadCANumberById({
            id: selectedQuote.caNumber.id,
            includeRelations: true,
          }),
        );
      } else {
        // Clear construction data for custom quotes
        this.constructionSheet.set([]);
        this.processedConstructionSteps.set([]);
      }
    });

    // Effect to process selected CA number when it's loaded
    effect(() => {
      const selectedCANumber = this.selectedCANumber();

      if (
        selectedCANumber &&
        this.selectedQuote()?.caNumber &&
        !this.selectedQuote()?.caCustomDescription
      ) {
        this.processSelectedCANumber();
      }
    });
  }

  ngOnInit(): void {
    // Subscribe to route parameter changes
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const quoteId = params.get('id');
      if (quoteId) {
        this.fetchData();
      }
    });

    // Load item processes categories once
    this.store.dispatch(QuotesActions.loadItemProcessesCategories());
  }

  ngOnDestroy(): void {
    this.displayNotes.set([]);
    this.store.dispatch(QuotesActions.clearNote());
  }

  get quoteId() {
    return this.route.snapshot.paramMap.get('id');
  }

  getQuoteTotalTabsForBreak(index: number): SafeAny[] {
    const quote = this.selectedQuote();
    if (!quote?.quantityBreaks || !quote.quantityBreaks[index]) {
      return [];
    }

    const currentBreak = quote.quantityBreaks[index];
    const quantity = currentBreak?.quantity ?? 0;
    const pieces = currentBreak?.pieces ?? 0;
    const hasPieces = quote.hasPieces;

    // Use calculated total price from accordion data
    const totalPrice = this.totalPrices()[index] || 0;

    const tabs = [
      { quantity: quantity, label: 'Qty', suffix: 'ft', index: 0 },
      {
        firstValue: +totalPrice.toFixed(2),
        secondValue: quantity || 0,
        prefix: '$',
        suffix: 'ft',
        index: 1,
      },
      {
        firstValue: quantity ? +(totalPrice / quantity).toFixed(3) || 0 : 0,
        secondValue: 1,
        prefix: '$',
        suffix: 'ft',
        index: 2,
      },
    ];

    if (hasPieces) {
      tabs.push({
        firstValue: pieces ? +(totalPrice / pieces).toFixed(3) || 0 : 0,
        secondValue: null,
        prefix: '$',
        suffix: 'EA',
        index: 3,
      });
    }

    return tabs;
  }

  saveNote(): void {
    const notes = this.notesForm.get('notes')?.value;

    if (notes && this.quoteId) {
      this.store.dispatch(QuotesActions.saveNote({ quoteId: this.quoteId, notes }));
    }

    this.notesForm.get('notes')?.reset();
  }

  private setDetailsAccordionData(itemProcesses: SafeAny): void {
    if (!itemProcesses) return;

    this.copperData.set(this.generateUniqueIds(itemProcesses['COPPER'] || []));
    this.singleEndData.set(this.generateUniqueIds(itemProcesses['SINGLE_END'] || []));
    this.fillersAndMiscLaborData.set(this.generateUniqueIds(itemProcesses['OTHER'] || []));
    this.compoundsData.set(this.generateUniqueIds(itemProcesses['COMPOUNDS'] || []));
    this.teflonTapesData.set(this.generateUniqueIds(itemProcesses['TEFLON_TAPES'] || []));
    this.allTapesAndFillersData.set(
      this.generateUniqueIds(itemProcesses['ALL_TAPES_AND_FILLERS'] || []),
    );
  }

  private generateUniqueIds(items: SafeAny[]): SafeAny[] {
    let parentIndex = 0;
    let childIndex = 0;
    let currentParentId = '';

    return items.map((item: SafeAny) => {
      const isParent = item.isParent !== undefined ? item.isParent : true;
      let uniqueId: string;

      if (isParent) {
        uniqueId = parentIndex.toString();
        currentParentId = uniqueId;
        parentIndex++;
        childIndex = 1; // Reset child index for new parent
      } else {
        uniqueId = `${currentParentId}_${childIndex}`;
        childIndex++;
      }

      return {
        ...item,
        uniqueId,
        isParent,
        ftNeeded: item.quantityBreak,
      };
    });
  }

  private updateAccordions(): void {
    const categories = this.itemProcessesCategories() || [];
    const accordions = categories.map(
      (category: { value: string; color?: string; key: string }, index: number) => {
        const categoryData = this.getDataForCategory(category.key);

        return {
          title: category.value,
          color: this.categoryColors?.[category.key] || category.color || '#8a8a8a',
          key: category.key,
          data: categoryData,
          index,
        };
      },
    );

    this.quoteDetailAccordions.set(accordions);
  }

  private getDataForCategory(key: string): SafeAny[] {
    switch (key) {
      case 'OTHER':
        return this.fillersAndMiscLaborData();
      case 'COPPER':
        return this.copperData();
      case 'SINGLE_END':
        return this.singleEndData();
      case 'COMPOUNDS':
        return this.compoundsData();
      case 'TEFLON_TAPES':
        return this.teflonTapesData();
      case 'ALL_TAPES_AND_FILLERS':
        return this.allTapesAndFillersData();
      default:
        return [];
    }
  }

  private calculateTotalPrices(): void {
    const quote = this.selectedQuote();
    if (!quote?.quantityBreaks) return;

    const totalPrices = new Array(quote.quantityBreaks.length).fill(0);

    // Calculate total prices from all accordion data
    const allAccordions = this.quoteDetailAccordions();
    allAccordions.forEach((accordion) => {
      accordion.data.forEach((item: SafeAny) => {
        if (item.sellPrice) {
          if (item.isParent) {
            totalPrices[0] = (totalPrices[0] || 0) + Number(item.sellPrice);
          } else {
            const uniqueIdParts = item.uniqueId?.split('_');
            const uniqueIdIndex = Number(uniqueIdParts?.[1]);
            if (!isNaN(uniqueIdIndex) && uniqueIdIndex < totalPrices.length) {
              totalPrices[uniqueIdIndex] =
                (totalPrices[uniqueIdIndex] || 0) + Number(item.sellPrice);
            }
          }
        }
      });
    });

    this.totalPrices.set(totalPrices);
  }

  deleteQuote(): void {
    const dialogRef = this.dialog.open(DeleteQuoteDialogComponent, {
      data: { quoteId: this.quoteId },
    });

    dialogRef.afterClosed();
  }

  duplicateQuote(): void {
    if (!this.quoteId) return;

    const dialogRef = this.dialog.open(DuplicateQuoteDialogComponent, {
      data: { quoteId: this.selectedQuote()?.quoteId.toString(), id: this.selectedQuote()?.id },
    });

    dialogRef.afterClosed();
  }

  changeArchiveStatus(): void {
    const dialogRef = this.dialog.open(ChangeQuoteStatusDialogComponent, {
      data: { quoteId: this.quoteId, archived: this.selectedQuote().archived },
    });

    dialogRef.afterClosed();
  }

  exportQuote(): void {
    if (!this.quoteId) return;
    this.store.dispatch(QuotesActions.exportQuotesBundle({ quotesId: this.quoteId }));
  }

  viewAttachment(attachment: SafeAny): void {
    if (attachment.url) {
      window.open(attachment.url, '_blank');
    }
  }

  private fetchData(): void {
    if (this.quoteId) {
      this.store.dispatch(QuotesActions.loadSpecificQuote({ quoteId: +this.quoteId }));
    }
  }

  private processSelectedCANumber(): void {
    const selectedCANumber = this.selectedCANumber();

    if (!selectedCANumber?.constructionSteps?.[0]) {
      return;
    }

    const firstConstructionStep = selectedCANumber.constructionSteps[0];
    this.constructionText.set(firstConstructionStep.description);

    // Define properties that should be excluded from array processing
    const excludedKeys = ['constructionSteps', 'attachments', 'itemProcesses'];

    // Extract all array properties from the selected CA number, excluding specified keys
    const arrayProperties = Object.entries(selectedCANumber)
      .filter(([key, value]) => Array.isArray(value) && !excludedKeys.includes(key))
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

    // Extract array properties from the first construction step
    const firstConstructionStepArrayProperties: Record<string, string[]> = Object.entries(
      firstConstructionStep,
    )
      .filter(([key, value]) => Array.isArray(value) && !excludedKeys.includes(key))
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

    // This creates a flat array of options with name, label, checked status, and item value
    const newOptions = Object.entries(arrayProperties).flatMap(([key, value]) => {
      if (!Array.isArray(value)) {
        return [];
      }

      // Create a capitalized label for the option
      const label = key.charAt(0).toUpperCase() + key.slice(1);
      // Get corresponding array from first construction step
      const firstStepArray = firstConstructionStepArrayProperties[key] || [];

      // Create options array - either a single "N/A" option or mapped array of values
      const resultValue: SafeAny =
        value.length === 0
          ? [
              {
                name: key,
                label: `${label}: N/A`,
                checked: false,
                item: null,
              },
            ]
          : value.map((item: string) => ({
              name: key,
              label: `${label}: ${item || 'N/A'}`,
              checked: item && firstStepArray.includes(item),
              item,
            }));

      return resultValue;
    });

    // Update construction sheet signal if we have new options
    if (newOptions.length > 0) {
      this.constructionSheet.set(newOptions);
    }

    // Get remaining construction steps (all except the first one)
    const remainingConstructionSteps = selectedCANumber.constructionSteps.slice(1);

    const remainingDescriptions: string[] = remainingConstructionSteps.map(
      (step: SafeAny) => step.description,
    );

    this.remainingDescriptions.set(remainingDescriptions);

    // Process each remaining construction step through the same transformation
    const processedSteps = remainingConstructionSteps.map((step: SafeAny) => {
      // Extract array properties from the current step
      const stepArrayProperties: Record<string, string[]> = Object.entries(selectedCANumber)
        .filter(([key, value]) => Array.isArray(value) && !excludedKeys.includes(key))
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

      // Transform each step's properties into options
      return Object.entries(step).flatMap(([key, value]) => {
        if (!Array.isArray(value)) {
          return [];
        }

        const label = key.charAt(0).toUpperCase() + key.slice(1);
        const firstStepArray = stepArrayProperties[key] || [];

        // Create options for each array property in the step
        return value.length === 0
          ? [
              {
                name: key,
                label: `${label}: N/A`,
                checked: false,
                item: null as string | null,
              },
            ]
          : value.map((item: string) => ({
              name: key,
              label: `${label}: ${item || 'N/A'}`,
              checked: firstStepArray.includes(item),
              item: item as string | null,
            }));
      });
    });

    // Update the processed construction steps signal with the transformed data
    this.processedConstructionSteps.set(processedSteps);
  }
}
